import { useEffect, useMemo, useRef, useState } from 'react';
import { LessonNode, ThemeToggle, type LessonNodeState } from '@fluent/ui';
import './PilotCReview.css';

/**
 * Phase 1 Pilot C — interactive review for the <LessonNode> primitive.
 * Five states in isolation, a three-node row composition, a size scale,
 * and a mock skill-tree fragment. Deleted when Storybook is wired up.
 */
export function PilotCReview() {
  const [taps, setTaps] = useState<Record<string, number>>({});
  const bump = (k: string) => setTaps((p) => ({ ...p, [k]: (p[k] ?? 0) + 1 }));

  const states: LessonNodeState[] = ['locked', 'unlocked', 'in-progress', 'complete', 'golden'];

  return (
    <main className="pilot-c">
      <header className="pilot-c__hero">
        <p className="pilot-c__kicker">Phase 1 · Pilot C</p>
        <h1>LessonNode primitive</h1>
        <p className="pilot-c__lede">
          The round skill-tree button. 5 states. Same 3D physics as the Button.
        </p>
        <div className="pilot-c__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* 5 states in isolation */}
      <section className="pilot-c__section">
        <h2>Five states</h2>
        <p className="pilot-c__note">Tap each. Locked won&apos;t respond. The others press down 2px.</p>

        <div className="pilot-c__grid">
          {states.map((s) => (
            <div key={s} className="pilot-c__frame">
              <span className="pilot-c__frame-label">{s}</span>
              <LessonNode
                state={s}
                label={labelFor(s)}
                completed={completedFor(s)}
                total={4}
                onClick={() => bump(s)}
              />
              <span className="pilot-c__frame-meter">
                {s === 'locked' ? '—' : `${taps[s] ?? 0} taps`}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Composition — 3 nodes in a row as they'd appear on a skill tree */}
      <section className="pilot-c__section">
        <h2>Composition — 3 nodes in a row</h2>
        <p className="pilot-c__note">
          Verifies the nodes sit well together. Middle one is the &ldquo;next&rdquo; lesson — breathing animation draws the eye.
        </p>
        <div className="pilot-c__row">
          <LessonNode state="complete" label="გამარჯობა" />
          <LessonNode state="in-progress" label="ოჯახი" total={4} completed={2} breathe />
          <LessonNode state="locked" label="საჭმელი" />
        </div>
      </section>

      {/* Vertical path mock — closer to how the home screen will look */}
      <section className="pilot-c__section">
        <h2>Skill-tree fragment</h2>
        <p className="pilot-c__note">Duolingo-style serpentine path, phone width. Previews Phase 6 home.</p>
        <Path />
      </section>

      {/* Size scale */}
      <section className="pilot-c__section">
        <h2>Size scale</h2>
        <p className="pilot-c__note">Default is 72. Use 56 for dense layouts, 96 for featured lesson tiles.</p>
        <div className="pilot-c__row pilot-c__row--baseline">
          <LessonNode state="unlocked" size={56} label="56" />
          <LessonNode state="unlocked" size={72} label="72 · default" />
          <LessonNode state="unlocked" size={96} label="96" />
        </div>
      </section>

      <footer className="pilot-c__footer">
        <p>
          <b>Round 1.</b> Tell Jake: bubble feel, icon weights, ring thickness, breathe speed, golden gradient.
        </p>
      </footer>
    </main>
  );
}

/**
 * Serpentine skill-tree path.
 *
 * Nodes swing along a sine wave horizontally. Vertical spacing between
 * consecutive nodes is NOT fixed — it shrinks where the horizontal swing
 * is large and expands where the swing is small, so every step along
 * the path is the same visual length. Without this, the path feels
 * irregular (pairs crowd at the wave's peaks and spread at the crossings).
 */
function Path() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(375);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const layout = useMemo(() => {
    // Path is a single smooth sine wave. Nodes sit ON the wave at equally-
    // spaced t values. The path SVG and the node positions use the same
    // formula, so they align perfectly regardless of amplitude or cycles.
    // Amplitude capped so labels (max ~115px wide) clear the viewport edge.
    const amplitude = Math.min(Math.max((width - 130) / 2, 100), 200);
    const cycles = 2; // mirror-symmetric: starts and ends at x=0, first half reflects second half
    const verticalStep = 140; // node-to-node vertical distance
    const nodeHeight = 72;

    const n = PATH.length;
    const totalSpan = (n - 1) * verticalStep;
    const waveFn = (t: number) => Math.sin(t * Math.PI * cycles) * amplitude;

    const xs = PATH.map((_, i) => waveFn(i / (n - 1)));
    const ys = PATH.map((_, i) => i * verticalStep);
    const totalHeight = totalSpan + nodeHeight + 60;

    // Sample the sine wave densely for a smooth SVG path.
    const samples = 140;
    let d = '';
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const x = waveFn(t);
      const y = t * totalSpan + nodeHeight / 2; // center align with node center
      d += (i === 0 ? 'M' : 'L') + ` ${x.toFixed(2)} ${y.toFixed(2)}`;
    }

    return { xs, ys, totalHeight, svgPath: d };
  }, [width]);

  return (
    <div ref={containerRef} className="pilot-c__path" style={{ height: layout.totalHeight }}>
      <svg
        className="pilot-c__path-svg"
        width={width}
        height={layout.totalHeight}
        viewBox={`${-width / 2} 0 ${width} ${layout.totalHeight}`}
        preserveAspectRatio="xMidYMin meet"
        aria-hidden="true"
      >
        <path className="pilot-c__path-stroke" d={layout.svgPath} />
      </svg>
      {PATH.map((n, i) => (
        <div
          key={i}
          className="pilot-c__path-node"
          style={{
            top: `${layout.ys[i]}px`,
            transform: `translateX(calc(-50% + ${layout.xs[i]}px))`,
          }}
        >
          <LessonNode
            state={n.state}
            label={n.label}
            total={n.total}
            completed={n.completed}
            breathe={n.breathe}
          />
        </div>
      ))}
    </div>
  );
}

const PATH: Array<{
  state: LessonNodeState;
  label: string;
  total?: number;
  completed?: number;
  breathe?: boolean;
}> = [
  { state: 'golden', label: 'შესავალი' },
  { state: 'complete', label: 'გამარჯობა' },
  { state: 'complete', label: 'რიცხვები' },
  { state: 'complete', label: 'ფერები' },
  { state: 'in-progress', label: 'ოჯახი', total: 4, completed: 2, breathe: true },
  { state: 'locked', label: 'საჭმელი' },
  { state: 'locked', label: 'სკოლა' },
  { state: 'locked', label: 'სამუშაო' },
  { state: 'locked', label: 'ცხოველები' },
  { state: 'locked', label: 'ამინდი' },
];

function labelFor(state: LessonNodeState): string {
  switch (state) {
    case 'locked':
      return 'ჩაკეტილი';
    case 'unlocked':
      return 'დაიწყე';
    case 'in-progress':
      return 'მიმდინარე';
    case 'complete':
      return 'დასრულდა';
    case 'golden':
      return 'ოქროს';
  }
}

function completedFor(state: LessonNodeState): number {
  switch (state) {
    case 'locked':
    case 'unlocked':
      return 0;
    case 'in-progress':
      return 2;
    case 'complete':
    case 'golden':
      return 4;
  }
}
